import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, Clock, MapPin, Send, CheckCircle2, Loader2, ImagePlus, X } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { SEO } from "../components/SEO";
import { BUSINESS } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = ["House", "Townhouse", "Condo / Apartment", "Small Office", "Other"];
const SIZES = ["Under 1,500 sq ft", "1,500 – 2,500 sq ft", "2,500 – 3,500 sq ft", "3,500+ sq ft", "Not sure"];
const CONTACT_METHODS = ["Phone Call", "Text Message", "Email"];
const VISIT_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "I'm flexible"];
const VISIT_TIMES = ["Morning (8:30–11am)", "Midday (11am–1pm)", "Afternoon (1–4:30pm)", "I'm flexible"];

const initial = {
  name: "",
  phone: "",
  email: "",
  location: "",
  property_type: "",
  approx_size: "",
  cleaning_needs: "",
  preferred_contact: "",
  pref_day: "",
  pref_time: "",
  alt_day: "",
  alt_time: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => [...p, res.data]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload that photo. Please try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      const pref = [form.pref_day, form.pref_time].filter(Boolean).join(", ");
      const alt = [form.alt_day, form.alt_time].filter(Boolean).join(", ");
      const availability = [pref && `Preferred: ${pref}`, alt && `Backup: ${alt}`].filter(Boolean).join(" · ");
      await axios.post(`${API}/leads`, { ...form, availability, photos });
      setDone(true);
      toast.success("Thank you! We'll be in touch within 24 hours to arrange your free home visit.");
      setForm(initial);
      setPhotos([]);
    } catch (err) {
      toast.error("Something went wrong. Please call or text us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact & Free Home Visit"
        description="Contact Wisp Home Cleaning to book your free home visit. Call or text 289-799-2690 or send a message about your Hamilton-area home or office."
        image="/life-professional.jpg"
      />
      <section className="border-b border-slate-200 bg-white px-5 pt-16 pb-14 md:px-10">
        <div className="mx-auto max-w-7xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Get in touch</span>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-light tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Book your free home visit!
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
            We stay in constant communication with our customers until the job is done. To book your
            free home visit, or if you have questions or special requests, just drop us a line.
          </p>
          <Link
            to="/guides/house-cleaning-cost-hamilton"
            data-testid="contact-cost-guide-link"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:underline"
          >
            Curious about cost? Read our honest Hamilton price guide →
          </Link>
        </div>
      </section>

      <section className="px-5 py-16 md:px-10">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1fr_1.6fr]">
          {/* Contact info */}
          <div className="space-y-4">
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <img
                src="/life-professional.jpg"
                alt="A happy customer relaxing at home after a Wisp clean"
                className="h-48 w-full object-cover"
              />
            </div>
            <a
              href={BUSINESS.phoneHref}
              data-testid="contact-call-link"
              className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-heading font-semibold text-slate-900">Call or Text</span>
                <span className="text-slate-600">{BUSINESS.phone}</span>
              </span>
            </a>
            <a
              href={BUSINESS.emailHref}
              data-testid="contact-email-link"
              className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Mail className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-heading font-semibold text-slate-900">Email</span>
                <span className="break-all text-slate-600">{BUSINESS.email}</span>
              </span>
            </a>
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Clock className="h-5 w-5" />
                </span>
                <span className="font-heading font-semibold text-slate-900">Hours</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                {BUSINESS.hours.map((h) => (
                  <li key={h.day} className="flex justify-between gap-4">
                    <span>{h.day}</span>
                    <span className="font-medium text-slate-800">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
              <MapPin className="h-5 w-5 text-primary" /> Serving {BUSINESS.region}
            </div>
          </div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm md:p-10"
          >
            {done ? (
              <div data-testid="contact-success" className="flex flex-col items-center justify-center py-16 text-center">
                <CheckCircle2 className="h-14 w-14 text-primary" />
                <h2 className="mt-5 font-heading text-2xl font-medium text-slate-900">Thank you! 💚</h2>
                <p className="mt-3 max-w-md text-slate-600">
                  We've received your message and we're so glad you reached out. A friendly member of
                  the Wisp team will get back to you within 24 hours to arrange your free home visit.
                </p>
                <button
                  data-testid="contact-reset-button"
                  onClick={() => setDone(false)}
                  className="mt-6 rounded-full border-2 border-slate-300 px-6 py-2.5 font-semibold text-slate-800 transition-colors hover:border-primary hover:text-primary"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Your full name" className="mt-2" required />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" type="tel" data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="289-000-0000" className="mt-2" required />
                  </div>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-2" />
                  </div>
                  <div>
                    <Label htmlFor="location">Address or Neighbourhood</Label>
                    <Input id="location" data-testid="input-location" value={form.location} onChange={set("location")} placeholder="e.g. Ancaster" className="mt-2" />
                  </div>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label>Property Type</Label>
                    <Select value={form.property_type} onValueChange={set("property_type")}>
                      <SelectTrigger data-testid="select-property-type" className="mt-2">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_TYPES.map((p) => (
                          <SelectItem key={p} value={p} data-testid={`property-option-${p}`}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Approximate Size</Label>
                    <Select value={form.approx_size} onValueChange={set("approx_size")}>
                      <SelectTrigger data-testid="select-size" className="mt-2">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        {SIZES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`size-option-${s}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="cleaning_needs">Tell us about your home</Label>
                  <Textarea
                    id="cleaning_needs"
                    data-testid="input-cleaning-needs"
                    value={form.cleaning_needs}
                    onChange={set("cleaning_needs")}
                    placeholder="Tell us a little about your home and what you're looking for — recurring cleaning, a one-time deep clean, specific rooms, or any special requests."
                    className="mt-2 min-h-[110px]"
                  />
                </div>

                <div>
                  <Label>Preferred Contact Method</Label>
                  <Select value={form.preferred_contact} onValueChange={set("preferred_contact")}>
                    <SelectTrigger data-testid="select-contact-method" className="mt-2">
                      <SelectValue placeholder="How should we reach you?" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_METHODS.map((m) => (
                        <SelectItem key={m} value={m} data-testid={`contact-method-option-${m}`}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-muted/60 p-5">
                  <Label className="text-base font-semibold text-slate-800">When works for your free visit?</Label>
                  <p className="mt-1 text-sm text-slate-500">
                    Pick a day and time that suit you — it's just a preference to get us started. We'll confirm the exact time with you, and we visit Mon–Fri, 8:30am–4:30pm.
                  </p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label>Preferred day</Label>
                      <Select value={form.pref_day} onValueChange={set("pref_day")}>
                        <SelectTrigger data-testid="select-pref-day" className="mt-2">
                          <SelectValue placeholder="Choose a day" />
                        </SelectTrigger>
                        <SelectContent>
                          {VISIT_DAYS.map((d) => (
                            <SelectItem key={d} value={d} data-testid={`pref-day-option-${d}`}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Preferred time</Label>
                      <Select value={form.pref_time} onValueChange={set("pref_time")}>
                        <SelectTrigger data-testid="select-pref-time" className="mt-2">
                          <SelectValue placeholder="Choose a time" />
                        </SelectTrigger>
                        <SelectContent>
                          {VISIT_TIMES.map((t) => (
                            <SelectItem key={t} value={t} data-testid={`pref-time-option-${t}`}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Backup day <span className="font-normal text-slate-400">(optional)</span></Label>
                      <Select value={form.alt_day} onValueChange={set("alt_day")}>
                        <SelectTrigger data-testid="select-alt-day" className="mt-2">
                          <SelectValue placeholder="Another day that works" />
                        </SelectTrigger>
                        <SelectContent>
                          {VISIT_DAYS.map((d) => (
                            <SelectItem key={d} value={d} data-testid={`alt-day-option-${d}`}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Backup time <span className="font-normal text-slate-400">(optional)</span></Label>
                      <Select value={form.alt_time} onValueChange={set("alt_time")}>
                        <SelectTrigger data-testid="select-alt-time" className="mt-2">
                          <SelectValue placeholder="Another time that works" />
                        </SelectTrigger>
                        <SelectContent>
                          {VISIT_TIMES.map((t) => (
                            <SelectItem key={t} value={t} data-testid={`alt-time-option-${t}`}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Photos of your space <span className="font-normal text-slate-400">(optional)</span></Label>
                  <p className="mt-1 text-sm text-slate-500">
                    Attach a few photos to help us prepare for your home visit.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    {photos.map((p) => (
                      <div key={p.id} data-testid={`photo-thumb-${p.id}`} className="group relative h-20 w-20 overflow-hidden rounded-xl border border-slate-200">
                        <img
                          src={`${API}/files/${p.path}`}
                          alt={p.filename}
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          data-testid={`remove-photo-${p.id}`}
                          onClick={() => removePhoto(p.id)}
                          className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100"
                          aria-label="Remove photo"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <label
                      data-testid="photo-upload-label"
                      className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 text-slate-400 transition-colors hover:border-primary hover:text-primary"
                    >
                      {uploading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <ImagePlus className="h-5 w-5" />
                          <span className="text-[11px] font-medium">Add</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        data-testid="photo-upload-input"
                        onChange={handleFiles}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 font-semibold text-primary-foreground shadow-[0_6px_20px_rgb(42,157,143,0.4)] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-70"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" /> Send a Message
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-slate-400">
                  We'll get back to you within one business day. We'll never quote guaranteed prices
                  sight unseen — every quote follows a free home visit.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </>
  );
}
